import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { IMG } from "@/data/content";

const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay }) => {
  const reduce = useReducedMotion();
  return (
    <span className="mask-line">
      <motion.span
        className="block"
        initial={reduce ? false : { y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.9, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
};

export default function Hero() {
  return (
    <section className="relative overflow-hidden bg-navy pt-[74px] text-white md:pt-[88px]">
      <div className="grid-texture pointer-events-none absolute inset-0 opacity-50" />

      {/* Darkened power-plant photo behind the navy, shifted to the right */}
      <div
        className="pointer-events-none absolute inset-0 bg-cover bg-right opacity-30"
        style={{ backgroundImage: `url(${IMG.heroTower})` }}
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-navy/25" />

      {/* Ambient glows */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -right-40 -top-24 h-[440px] w-[440px] rounded-full bg-electric/[0.12] blur-[130px]" />
        <div className="absolute -left-32 top-1/3 h-[380px] w-[380px] rounded-full bg-energy/[0.1] blur-[130px]" />
      </div>

      <div className="relative mx-auto max-w-shell px-6 py-24 md:px-12 md:py-32 lg:py-36">
        <div className="max-w-4xl">
          <h1 className="font-heading text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            <Line delay={0.15}>Commercial Energy Solutions</Line>
            <Line delay={0.27}>Built Around Your Business.</Line>
          </h1>

          <Reveal delay={0.55}>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/70">
              Innervation Energy Group delivers electricity, natural gas,
              battery storage, and innovative energy solutions designed to
              reduce costs, improve reliability, and support commercial
              operations.
            </p>
          </Reveal>

          <Reveal delay={0.85}>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/contact"
                data-testid="hero-contact-btn"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-electric px-8 py-4 text-sm font-medium text-white transition-colors hover:bg-white hover:text-navy"
              >
                Request a Consultation
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="#how-it-works"
                data-testid="hero-how-btn"
                className="group inline-flex items-center justify-center gap-2 rounded-md border border-white/25 px-8 py-4 text-sm font-medium text-white transition-colors hover:border-energy hover:text-energy"
              >
                See How It Works
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </Reveal>

          <Reveal delay={1}>
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.18em] text-white/50">
              Electricity · Natural Gas · Virtual Power Plant Network
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
